import { HttpHeaders } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { COMPONENT_ID_HEADER } from '../../../core/consts/api.const';
import {
  FFResultData,
  SimulationResponse,
  FFSimulationResults,
} from '../../../core/models/simulation.model';
import { LoadingService } from '../../../services/loading.service';
import { SimulationsService } from '../../../services/simulations/simulations.service';
import { Destroyable } from '../../../utils/mixins/destroyable.mixin';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatExpansionModule } from '@angular/material/expansion';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SimulationOverviewComponent } from '../../simulation-overview/simulation-overview.component';
import { SimulationDirectionsTableComponent } from '../../simulation-directions-table/simulation-directions-table.component';
import { map, merge } from 'rxjs';
import { formatValue } from '../../../utils/format.util';
import { SimulatorType } from '../../../core/enums/simulator-type.enum';

@Component({
  selector: 'app-formation-factor-simulation-results',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatTabsModule,
    MatChipsModule,
    MatTableModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    MatExpansionModule,
    SimulationOverviewComponent,
    SimulationDirectionsTableComponent
],
  templateUrl: './formation-factor-simulation-results.component.html',
  styleUrl: './formation-factor-simulation-results.component.scss',
})
export class FormationFactorSimulationResultsComponent
  extends Destroyable(Object)
  implements OnInit
{
  simulation: SimulationResponse<FFSimulationResults> | null = null;
  simulationId: string | null = null;
  isLoading = false;
  directions: string[] = [];
  directionsForm!: FormGroup;
  allResultsInvalid: boolean = false;
  displayedColumns: string[] = ['direction', 'totalPorosity', 'connectedPorosity', 'formationFactor'];
  tableData: any[] = [];

  readonly componentId = 'ff-simulation-results';

  get simulationResponse(): SimulationResponse<FFSimulationResults> | null {
    return this.simulation;
  }

  get pageTitle(): string {
    const prefix =
      this.simulation?.type === SimulatorType.UpscalingFF
        ? 'Upscaling - '
        : 'Subsample - ';
    return prefix + 'Formation Factor Results';
  }

  constructor(
    private route: ActivatedRoute,
    private simulationsService: SimulationsService,
    private loadingService: LoadingService,
    private fb: FormBuilder,
  ) {
    super();
  }

  ngOnInit(): void {
    this.simulationId = this.route.snapshot.paramMap.get('id');
    this.directions = ['x', 'y', 'z'];
    this.initDirectionForm();
    this.subscribeToLoadingState();

    if (this.simulationId) {
      this.loadSimulation(this.simulationId);
    }
  }

  getDirectionControl(direction: string): FormControl {
    return this.directionsForm.get(direction) as FormControl;
  }

  private loadSimulation(id: string): void {
    this.loadingService.startLoading(this.componentId);

    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: this.componentId,
      }),
    };

    this.simulationsService
      .getSimulationById(id, options)
      .pipe(this.takeUntilDestroyed())
      .subscribe((simulation) => {
        this.simulation = simulation as SimulationResponse<FFSimulationResults>;

        this.configureDirectionControls();
        this.updateTable();

        this.allResultsInvalid = this.directions.every(
          (direction) => !this.isResultValid(direction),
        );
      });
  }

  downloadResults(): void {
    if (this.simulation && this.checkIfAtLeastOneDirectionSelected()) {
      const directions: Record<string, boolean> = this.getSelectedDirections();
      this.simulationsService.downloadResult(this.simulation.id, directions);
    }
  }

  getResultByDirection(direction: string): FFResultData | undefined {
    return this.simulation?.results?.find(
      (result) => result.direction.toLowerCase() === direction.toLowerCase(),
    )?.result;
  }

  hasAnyResults(): boolean {
    return !!this.simulation?.results && this.simulation.results.length > 0;
  }

  isResultValid(direction: string): boolean {
    const directionResult = this.simulation?.results?.find(
      (result) => result.direction.toLowerCase() === direction.toLowerCase(),
    );

    return directionResult?.status?.toLowerCase() === 'success';
  }

  private configureDirectionControls(): void {
    ['x', 'y', 'z'].forEach((direction: string) => {
      const hasValidResultForDirection: boolean = this.isResultValid(direction);

      if (hasValidResultForDirection) {
        this.getDirectionControl(direction).enable({ emitEvent: false });
        this.getDirectionControl(direction).patchValue(true, { emitEvent: false });
      } else {
        this.getDirectionControl(direction).patchValue(false, { emitEvent: false });
        this.getDirectionControl(direction).disable({ emitEvent: false });
      }
    });
  }

  private updateTable(): void {
    this.tableData = this.directions
      .filter(direction => this.getDirectionControl(direction)?.value)
      .map((direction) => {
        const result = this.getResultByDirection(direction);
        const totalPorosity = result?.totalPorosity != null 
          ? (result.totalPorosity < 1 ? result.totalPorosity * 100 : result.totalPorosity)
          : null;
        const connectedPorosity = result?.connectedPorosity != null 
          ? (result.connectedPorosity < 1 ? result.connectedPorosity * 100 : result.connectedPorosity)
          : null;
        return {
          direction: direction.toUpperCase(),
          totalPorosity: totalPorosity != null ? formatValue(String(totalPorosity)) : '—',
          connectedPorosity: connectedPorosity != null ? formatValue(String(connectedPorosity)) : '—',
          formationFactor: result?.formationFactor != null ? formatValue(String(result.formationFactor)) : '—',
        };
      });
  }

  private initDirectionForm(): void {
    this.directionsForm = this.fb.group({
      x: [{ value: false, disabled: true }],
      y: [{ value: false, disabled: true }],
      z: [{ value: false, disabled: true }],
    });

    merge(
      this.getDirectionControl('x').valueChanges.pipe(map(value => ({ direction: 'x', value }))),
      this.getDirectionControl('y').valueChanges.pipe(map(value => ({ direction: 'y', value }))),
      this.getDirectionControl('z').valueChanges.pipe(map(value => ({ direction: 'z', value }))),
    )
      .pipe(this.takeUntilDestroyed())
      .subscribe(() => {
        this.updateTable();
      });
  }

  private getSelectedDirections(): Record<string, boolean> {
    return {
      directionX: this.getDirectionControl('x').value,
      directionY: this.getDirectionControl('y').value,
      directionZ: this.getDirectionControl('z').value,
    };
  }

  public checkIfAtLeastOneDirectionSelected(): boolean {
    const selectedDirections: Record<string, boolean> = this.getSelectedDirections();
    return Object.values(selectedDirections).some(Boolean);
  }

  private subscribeToLoadingState(): void {
    this.loadingService
      .getLoadingState(this.componentId)
      .pipe(this.takeUntilDestroyed())
      .subscribe((isLoading) => {
        this.isLoading = isLoading;
      });
  }
}
